import React, { useEffect, useRef, useReducer, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExportUtils, ExportData } from './utils/exportUtils';
import { usePatientData } from './components/SimpleWISCV/hooks/usePatientData';
import { useAIIntegration, PARTIAL_STREAM_STORAGE_KEY } from './components/SimpleWISCV/hooks/useAIIntegration';
import { usePromptLibrary } from './components/SimpleWISCV/hooks/usePromptLibrary';
import { useStreaming } from './components/SimpleWISCV/hooks/useStreaming';
import { Header } from './components/SimpleWISCV/components/Header';
import { PatientInfoForm } from './components/SimpleWISCV/components/PatientInfoForm';
import { SubtestScoreInput, CompositeScoreInput } from './components/SimpleWISCV/components/ScoreInput';
import { AIControls } from './components/SimpleWISCV/components/AIControls';
import { ResultsDisplay } from './components/SimpleWISCV/components/ResultsDisplay';
import { FullscreenResultDialog } from './components/SimpleWISCV/components/FullscreenResultDialog';
import { buildInterpretationPlainText } from './components/SimpleWISCV/components/InterpretationSections';
import aiService from './services/aiService';
import { SavedDiagnosesCard } from './components/SimpleWISCV/components/SavedDiagnosesCard';
import {
  listSavedDiagnoses,
  saveDiagnosis,
  updateDiagnosis,
  removeDiagnosis,
  clearDiagnoses,
  SavedDiagnosis,
} from './components/SimpleWISCV/utils/diagnosesStore';
import { PromptLibraryModal } from './components/SimpleWISCV/components/PromptLibraryModal';
import { PromptEditorModal } from './components/SimpleWISCV/components/PromptEditorModal';
import { PromptPreviewModal } from './components/SimpleWISCV/components/PromptPreviewModal';
import { DebugLog } from './components/SimpleWISCV/components/DebugLog';
import { CognitiveProfileAnalyzer } from './components/SimpleWISCV/components/CognitiveProfileAnalyzer';
import { AIConfigProvider, AIConfigContextValue } from './components/SimpleWISCV/context/AIConfigContext';
import { compositeNames, subtestNames } from './components/SimpleWISCV/utils/constants';
import { getScoreInterpretation, collectScoreValidationErrors } from './components/SimpleWISCV/utils/helpers';
import type { Interpretation, PatientInfo } from './components/SimpleWISCV/types';
import { runEngineShadow } from './services/engineShadow';

// sessionStorage key for the legacy UI state (per-tab; cleared when the tab closes — safer for GDPR than localStorage)
const SESSION_STORAGE_KEY = 'wiscv-legacy-state-v1';

// Marker w SavedDiagnosis.editedSections oznaczający redakcję CAŁOŚCI diagnozy
// (edytor markdown, plan 4.2). Schemat wpisu (string[]) zostaje bez zmian —
// badge w SavedDiagnosesCard reaguje na niepustą listę.
const WHOLE_DIAGNOSIS_EDIT_MARKER = '__whole-diagnosis__';

interface PersistedLegacyState {
  patientInfo?: Partial<PatientInfo>;
  subtestScores?: Record<string, string>;
  compositeScores?: Record<string, string>;
  interpretation?: Interpretation | null;
  /** Diagnoza była ręcznie redagowana w edytorze całości (badge „edytowano"). */
  diagnosisEdited?: boolean;
  /**
   * LEGACY (fala per-sekcja, zastąpiona przez edycję całości — plan 4.2):
   * klucze zredagowanych sekcji. Czytane tylko przy restore starych sesji
   * (niepuste = diagnosisEdited); nie zapisujemy go już.
   */
  editedSections?: string[];
  /** Id wpisu w zapisanych diagnozach, który aktualizują edycje. */
  currentDiagnosisId?: string | null;
  savedAt?: number;
}

const readPersistedState = (): PersistedLegacyState | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as PersistedLegacyState;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// UI state reducer (wave 2.6) — related useState pieces consolidated into one
// reducer: loading overlay, export dropdown, copy toast, the dismissed flag of
// the recovered-partial banner and the "is there anything to retry?" flag.
// ---------------------------------------------------------------------------
export interface UIState {
  loading: boolean;
  loadingMessage: string;
  showExportDropdown: boolean;
  copyToast: { show: boolean; message: string };
  recoveredPartialDismissed: boolean;
  generationAttempted: boolean;
}

export type UIAction =
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_LOADING_MESSAGE'; message: string }
  | { type: 'SET_EXPORT_DROPDOWN'; show: boolean }
  | { type: 'SHOW_COPY_TOAST'; message: string }
  | { type: 'HIDE_COPY_TOAST' }
  | { type: 'DISMISS_RECOVERED_PARTIAL' }
  | { type: 'GENERATION_ATTEMPTED' };

export const initialUIState: UIState = {
  loading: false,
  loadingMessage: '',
  showExportDropdown: false,
  copyToast: { show: false, message: '' },
  recoveredPartialDismissed: false,
  generationAttempted: false,
};

export const uiReducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_LOADING_MESSAGE':
      return { ...state, loadingMessage: action.message };
    case 'SET_EXPORT_DROPDOWN':
      return { ...state, showExportDropdown: action.show };
    case 'SHOW_COPY_TOAST':
      return { ...state, copyToast: { show: true, message: action.message } };
    case 'HIDE_COPY_TOAST':
      return { ...state, copyToast: { show: false, message: '' } };
    case 'DISMISS_RECOVERED_PARTIAL':
      return { ...state, recoveredPartialDismissed: true };
    case 'GENERATION_ATTEMPTED':
      return { ...state, generationAttempted: true };
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Zwijane karty (CollapsibleCard) — mapa {cardId: open}. Stan żyje obok
// uiReducer jako lokalna mapa (spójnie ze wzorcem dozwolonym w zleceniu;
// initialUIState jest snapshot-testowany przez toEqual, więc nie rozszerzamy
// jego kształtu).
// ---------------------------------------------------------------------------
type CardId = 'patient' | 'ocr' | 'subtests' | 'composites' | 'profile' | 'ai' | 'saved' | 'debug';

/** Karty wejściowe zwijane automatycznie na starcie generacji AI. */
const INPUT_CARD_IDS: CardId[] = ['patient', 'ocr', 'subtests', 'composites', 'profile'];

const initialCardsOpen: Record<CardId, boolean> = {
  patient: true,
  ocr: true,
  subtests: true,
  composites: true,
  profile: true,
  ai: true,
  saved: false, // Zapisane diagnozy — domyślnie zwinięta
  debug: true,
};

/** Krótki komunikat po kopiowaniu/zapisie — szkło pływające, w portalu (z-toast). */
const CopyToast: React.FC<{ message: string }> = ({ message }) =>
  createPortal(
    <div
      role="status"
      aria-live="polite"
      className="glass-float fixed z-toast bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 flex items-center gap-3 rounded-xl px-5 py-3 text-txt-primary animate-fadeIn print:hidden"
    >
      <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-accent" />
      <span className="text-sm font-semibold">{message}</span>
    </div>,
    document.body
  );

const SimpleWISCVSystem = () => {
  const [ui, dispatch] = useReducer(uiReducer, initialUIState);

  // Stable setter shims for hooks that expect plain setters
  const setLoading = useCallback((loading: boolean) => dispatch({ type: 'SET_LOADING', loading }), []);
  const setLoadingMessage = useCallback((message: string) => dispatch({ type: 'SET_LOADING_MESSAGE', message }), []);
  const setShowExportDropdown = useCallback((show: boolean) => dispatch({ type: 'SET_EXPORT_DROPDOWN', show }), []);

  // Custom Hooks
  const {
    enableStreaming,
    streamingText,
    rawStreamText,
    isStreaming,
    streamContainerRef,
    rawStreamTextRef,
    setEnableStreaming,
    setStreamingText,
    setRawStreamText,
    setIsStreaming
  } = useStreaming();

  const {
    patientInfo,
    subtestScores,
    compositeScores,
    loadTestData,
    handlePatientInfoChange,
    handleSubtestScoreChange,
    handleCompositeScoreChange,
    handleImageUpload
  } = usePatientData(setLoading, setLoadingMessage);

  const {
    loading: aiLoading, // Rename to avoid conflict with local loading state if needed, but we use local loading too
    loadingMessage: aiLoadingMessage,
    debugLogs,
    aiConnectionStatus,
    isTestingConnection,
    availableModels,
    geminiModels,
    geminiCategories,
    modelNotice,
    selectedModel,
    selectedProvider,
    fallbackModel,
    loadingModels,
    enableThinking,
    thinkingBudget,
    thinkingProcess,
    interpretation,
    showRawResponse,
    savedRawText,
    recoveredPartialText,
    lastGenerationStatus,
    firstChunkReceived,
    answeredModel,
    generationNotice,
    setDebugLogs,
    setSelectedModel,
    setSelectedProvider,
    setFallbackModel,
    setEnableThinking,
    setThinkingBudget,
    setShowRawResponse,
    setInterpretation,
    setSavedRawText,
    testAIConnection,
    loadFreeModels,
    loadGeminiModels,
    generateInterpretation,
    retryGeneration,
    resetGenerationArtifacts
  } = useAIIntegration(setStreamingText, setRawStreamText, setIsStreaming, rawStreamTextRef);

  const {
    promptLibrary,
    selectedPromptId,
    selectedSystemPrompt,
    previewSystemPrompt,
    showPromptLibrary,
    editingPrompt,
    showPromptPreview,
    previewPrompt,
    setEditingPrompt,
    setShowPromptLibrary,
    setSelectedPromptId,
    setShowPromptPreview,
    preparePrompt,
    createNewPrompt,
    editPrompt,
    savePrompt,
    deletePrompt,
    viewPrompt,
    copyPromptToClipboard
  } = usePromptLibrary(patientInfo, subtestScores, compositeScores);

  // Sync loading state from AI hook
  // Actually, useAIIntegration manages its own loading state.
  // We can use `loading || aiLoading` for disabled states.
  const isGlobalLoading = ui.loading || aiLoading;
  const globalLoadingMessage = ui.loading ? ui.loadingMessage : aiLoadingMessage;

  // ---------------------------------------------------------------------------
  // Zwijane karty — stan otwarcia + automatyka wokół generacji AI
  // ---------------------------------------------------------------------------
  const [openCards, setOpenCards] = useState<Record<CardId, boolean>>(initialCardsOpen);

  // Pełnoekranowy widok wyniku (Radix Dialog) — działa też podczas streamingu.
  // Rozszerzanie = FLIP: overlay WYRASTA z prostokąta panelu wyniku. originRect
  // to zmierzony getBoundingClientRect panelu w chwili otwierania (null = brak
  // origin, np. auto-otwarcie na starcie generacji albo prefers-reduced-motion →
  // dialog robi zwykły fade). NIE zmienia stanu strony pod spodem (overlay-only).
  const [showFullscreenResult, setShowFullscreenResult] = useState(false);
  const [fullscreenOriginRect, setFullscreenOriginRect] = useState<DOMRect | null>(null);

  // ---------------------------------------------------------------------------
  // Edycja diagnozy JAKO CAŁOŚCI (plan 4.2) + zapisane diagnozy (localStorage —
  // świadomy wyjątek RODO, patrz diagnosesStore.ts / CLAUDE.md zasada 6)
  // ---------------------------------------------------------------------------
  // Diagnoza była ręcznie redagowana w edytorze całości (badge „edytowano",
  // confirm przed nową generacją); persystowane w wiscv-legacy-state-v1.
  const [diagnosisEdited, setDiagnosisEdited] = useState(false);

  // Id wpisu w zapisanych diagnozach, który aktualizują edycje
  // (auto-zapis po generacji / wczytany wpis).
  const [currentDiagnosisId, setCurrentDiagnosisId] = useState<string | null>(null);

  // Lista zapisanych diagnoz — lustrzane odbicie localStorage, odświeżane po
  // każdej operacji na storze.
  const [savedDiagnoses, setSavedDiagnoses] = useState<SavedDiagnosis[]>(() => listSavedDiagnoses());

  const setCardOpen = useCallback((id: CardId, open: boolean) => {
    setOpenCards(prev => (prev[id] === open ? prev : { ...prev, [id]: open }));
  }, []);

  // Para open/onOpenChange dla CollapsibleCard danej karty
  const cardControl = useCallback(
    (id: CardId) => ({ open: openCards[id], onOpenChange: (open: boolean) => setCardOpen(id, open) }),
    [openCards, setCardOpen]
  );

  // Dynamika generacji — choreografia sterowana PIERWSZYM TOKENEM (plan 5.2):
  // - W1 „Łączenie" (aiLoading: false→true, zero tokenów): ŻADNEJ zmiany
  //   layoutu — tylko spinner CTA (isGlobalLoading) + Dziennik. Krawędź
  //   aiLoading zeruje wyłącznie diagnosisEdited/currentDiagnosisId.
  // - W2 „Pierwszy token" (firstChunkReceived: false→true): JEDNA sekwencja —
  //   zwinięcie kart wejściowych + otwarcie arkusza FLIP z prostokąta panelu
  //   wyniku (rect z refu; null/zerowy → fade). Efekt niżej.
  // - W5 „Błąd przed pierwszym tokenem": firstChunkReceived nigdy nie wchodzi
  //   na true → nic się nie zwija, arkusz się nie otwiera, zero pulsu.
  // - koniec generacji (true→false): przy SUKCESIE zwiń też „Konfigurację
  //   Silnika AI" (użytkownik patrzy na wynik); przy BŁĘDZIE upewnij się, że
  //   jest rozwinięta — tam mieszka przycisk „Ponów".
  const prevAiLoadingRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevAiLoadingRef.current;
    prevAiLoadingRef.current = aiLoading;

    if (!wasLoading && aiLoading) {
      // Nowa generacja unieważnia ręczne redakcje (zeruje diagnosisEdited)
      // i odcina się od bieżącego wpisu zapisanych diagnoz (sukces utworzy
      // nowy wpis). Choreografia layoutu czeka na pierwszy token (W2).
      setDiagnosisEdited(false);
      setCurrentDiagnosisId(null);
    } else if (wasLoading && !aiLoading) {
      if (lastGenerationStatus === 'success') {
        setCardOpen('ai', false);
        // AUTO-ZAPIS: udana generacja trafia do zapisanych diagnoz
        // (localStorage — świadomy wyjątek RODO); kolejne edycje
        // aktualizują ten wpis przez currentDiagnosisId.
        if (interpretation) {
          const entry = saveDiagnosis({
            patientInfo,
            subtestScores,
            compositeScores,
            interpretation,
            editedSections: [],
          });
          if (entry) {
            setCurrentDiagnosisId(entry.id);
            setSavedDiagnoses(listSavedDiagnoses());
          }
        }
      } else if (lastGenerationStatus === 'error') {
        setCardOpen('ai', true);
      }
    }
  }, [aiLoading, lastGenerationStatus, setCardOpen, interpretation, patientInfo, subtestScores, compositeScores]);

  // W2 (plan 5.2): krawędź firstChunkReceived false→true = jedna skoordynowana
  // sekwencja: zwinięcie kart wejściowych + auto-otwarcie arkusza animacją FLIP
  // z prostokąta panelu wyniku. Rect mierzony z wrappera panelu (panel istnieje
  // w kolumnie 3; przy pierwszym tokenie karta streamu jest już wyrenderowana —
  // efekt biegnie po commicie tego samego batcha co chunk). Zerowy/nieobecny
  // rect → null → dialog robi fade.
  const resultsPanelWrapRef = useRef<HTMLDivElement>(null);
  const prevFirstChunkRef = useRef(false);
  useEffect(() => {
    const hadChunk = prevFirstChunkRef.current;
    prevFirstChunkRef.current = firstChunkReceived;
    if (hadChunk || !firstChunkReceived) return;

    setOpenCards(prev => {
      const next = { ...prev };
      INPUT_CARD_IDS.forEach(id => { next[id] = false; });
      return next;
    });
    const rect = resultsPanelWrapRef.current?.getBoundingClientRect() ?? null;
    setFullscreenOriginRect(rect && rect.width >= 1 && rect.height >= 1 ? rect : null);
    setShowFullscreenResult(true);
  }, [firstChunkReceived]);

  // AI configuration context value (wave 2.3) — feeds AIControls without prop
  // drilling. The state itself still lives in useAIIntegration/useAIProvider.
  const aiConfigValue = useMemo<AIConfigContextValue>(() => ({
    selectedProvider,
    setSelectedProvider,
    selectedModel,
    setSelectedModel,
    fallbackModel,
    setFallbackModel,
    enableThinking,
    setEnableThinking,
    thinkingBudget,
    setThinkingBudget,
    availableModels,
    geminiModels,
    geminiCategories,
    modelNotice,
    loadingModels,
    aiConnectionStatus,
    isTestingConnection,
    testAIConnection,
    loadFreeModels,
    loadGeminiModels,
  }), [
    selectedProvider, setSelectedProvider,
    selectedModel, setSelectedModel,
    fallbackModel, setFallbackModel,
    enableThinking, setEnableThinking,
    thinkingBudget, setThinkingBudget,
    availableModels, geminiModels, geminiCategories, modelNotice, loadingModels,
    aiConnectionStatus, isTestingConnection,
    testAIConnection, loadFreeModels, loadGeminiModels,
  ]);

  // ---------------------------------------------------------------------------
  // Persistence (sessionStorage) — protects against data loss on page reload
  // ---------------------------------------------------------------------------
  // The interpretation restored from sessionStorage goes straight into the
  // useAIIntegration hook via setInterpretation — the hook state is the single
  // source of truth (the local restoredInterpretation fallback was removed in
  // wave 2c).
  const hasRestoredRef = useRef(false);

  // (b) Restore state from sessionStorage on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const saved = readPersistedState();
    if (!saved) return;

    if (saved.patientInfo) {
      (Object.entries(saved.patientInfo) as Array<[keyof PatientInfo, string]>).forEach(([field, value]) => {
        if (typeof value === 'string' && value !== '') handlePatientInfoChange(field, value);
      });
    }
    if (saved.subtestScores) {
      Object.entries(saved.subtestScores).forEach(([key, value]) => {
        if (typeof value === 'string' && value !== '') handleSubtestScoreChange(key, value);
      });
    }
    if (saved.compositeScores) {
      Object.entries(saved.compositeScores).forEach(([key, value]) => {
        if (typeof value === 'string' && value !== '') handleCompositeScoreChange(key, value);
      });
    }
    if (saved.interpretation) {
      // Restored right after mount — ResultsDisplay suppresses its auto-scroll
      // for interpretations appearing within ~1.5 s of mount (mountedAtRef).
      setInterpretation(saved.interpretation);
    }
    // Flaga „edytowano": nowy format (diagnosisEdited) z fallbackiem na
    // legacy listę editedSections z fali per-sekcja (niepusta = edytowano).
    if (saved.diagnosisEdited === true || (Array.isArray(saved.editedSections) && saved.editedSections.length > 0)) {
      setDiagnosisEdited(true);
    }
    if (typeof saved.currentDiagnosisId === 'string') {
      setCurrentDiagnosisId(saved.currentDiagnosisId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (a) Save state to sessionStorage with debounce (500 ms)
  useEffect(() => {
    if (!hasRestoredRef.current) return; // never overwrite saved data before restore ran
    const timer = setTimeout(() => {
      try {
        const payload: PersistedLegacyState = {
          patientInfo,
          subtestScores,
          compositeScores,
          interpretation,
          diagnosisEdited,
          currentDiagnosisId,
          savedAt: Date.now(),
        };
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // Quota / privacy mode — persistence is best-effort only
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [patientInfo, subtestScores, compositeScores, interpretation, diagnosisEdited, currentDiagnosisId]);

  // (c) Warn before unload while an AI generation is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isGlobalLoading || isStreaming) {
        e.preventDefault();
        e.returnValue = ''; // required by Chrome to show the native prompt
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isGlobalLoading, isStreaming]);

  const handleCopySection = useCallback(async (title: string, content: string) => {
    try {
      await navigator.clipboard.writeText(`${title}\n\n${content}`);
      setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ✅ Skopiowano sekcję: ${title}`]);
      dispatch({ type: 'SHOW_COPY_TOAST', message: `✓ Skopiowano: ${title}` });
    } catch (error: any) {
      setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ❌ Błąd kopiowania: ${error.message}`]);
      dispatch({ type: 'SHOW_COPY_TOAST', message: `✗ Błąd kopiowania` });
    }
    setTimeout(() => {
      dispatch({ type: 'HIDE_COPY_TOAST' });
    }, 3000);
  }, [setDebugLogs]);

  // „Kopiuj całość" — wszystkie niepuste sekcje jako czysty tekst
  // (nagłówki + treść rozdzielone pustą linią, listy zaleceń jako „- pozycja").
  const handleCopyAll = useCallback(async () => {
    if (!interpretation) return;
    const text = buildInterpretationPlainText(interpretation);
    try {
      await navigator.clipboard.writeText(text);
      setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ✅ Skopiowano całość raportu (czysty tekst)`]);
      dispatch({ type: 'SHOW_COPY_TOAST', message: '✓ Skopiowano całość raportu' });
    } catch (error: any) {
      setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ❌ Błąd kopiowania: ${error.message}`]);
      dispatch({ type: 'SHOW_COPY_TOAST', message: '✗ Błąd kopiowania' });
    }
    setTimeout(() => {
      dispatch({ type: 'HIDE_COPY_TOAST' });
    }, 3000);
  }, [interpretation, setDebugLogs]);

  // „Kopiuj markdown" (pełny ekran) — surowa odpowiedź AI w formacie Markdown.
  const handleCopyMarkdown = useCallback(async () => {
    const markdown = savedRawText || rawStreamText;
    if (!markdown.trim()) return;
    try {
      await navigator.clipboard.writeText(markdown);
      setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ✅ Skopiowano pełną odpowiedź Markdown`]);
      dispatch({ type: 'SHOW_COPY_TOAST', message: '✓ Skopiowano markdown' });
    } catch (error: any) {
      setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ❌ Błąd kopiowania: ${error.message}`]);
      dispatch({ type: 'SHOW_COPY_TOAST', message: '✗ Błąd kopiowania' });
    }
    setTimeout(() => {
      dispatch({ type: 'HIDE_COPY_TOAST' });
    }, 3000);
  }, [savedRawText, rawStreamText, setDebugLogs]);

  // Otwarcie pełnego ekranu z panelu (Maximize2) — zapamiętujemy origin rect
  // panelu wyniku dla animacji FLIP, potem montujemy dialog.
  const handleOpenFullscreen = useCallback((originRect: DOMRect | null) => {
    setFullscreenOriginRect(originRect);
    setShowFullscreenResult(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Edycja diagnozy JAKO CAŁOŚCI (plan 4.2): zapis edytowanego markdownu
  // z FullscreenResultDialog → re-parse do sekcji (aiService.parseResponse-
  // Public) → setInterpretation (hook = jedyne źródło prawdy) → sessionStorage
  // i eksporty (downloadReport buduje exportData z interpretation) dostają
  // wersję po redakcji automatycznie. savedRawText jest aktualizowany na nową
  // treść, więc widok „Pełna odpowiedź Markdown" i kolejne wejścia w edycję
  // pracują na zredagowanym markdownie. Edycja aktualizuje też bieżący wpis
  // zapisanych diagnoz (update po currentDiagnosisId).
  // ---------------------------------------------------------------------------
  const handleSaveEditedMarkdown = useCallback((markdown: string) => {
    // Synchronizacja surowego markdownu PRZED setInterpretation (I-3): ref
    // natychmiast, stan od razu po nim — żeby natychmiastowe ponowne „Edytuj"
    // (źródło: rawText = savedRawText || rawStreamText) dostało nową treść,
    // nie starą.
    rawStreamTextRef.current = markdown;
    setSavedRawText(markdown);
    const nextInterpretation = aiService.parseResponsePublic(markdown);
    setInterpretation(nextInterpretation);
    setDiagnosisEdited(true);

    if (currentDiagnosisId) {
      const updated = updateDiagnosis(currentDiagnosisId, {
        interpretation: nextInterpretation,
        // Marker edycji całości — SavedDiagnosesCard pokazuje badge przy
        // niepustej liście (schemat wpisu pozostaje bez zmian).
        editedSections: [WHOLE_DIAGNOSIS_EDIT_MARKER],
      });
      if (updated) setSavedDiagnoses(listSavedDiagnoses());
    }
    setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString('pl-PL')}] ✏️ Zredagowano diagnozę (edytor całości)`]);
  }, [currentDiagnosisId, setInterpretation, setSavedRawText, setDebugLogs, rawStreamTextRef]);

  // Edycja dostępna TYLKO gdy nie trwa streaming/generacja i jest interpretacja
  // (FullscreenResultDialog dodatkowo sam pilnuje interpretation/isStreaming).
  const saveEditedMarkdownHandler =
    !isStreaming && !isGlobalLoading && interpretation ? handleSaveEditedMarkdown : undefined;

  // ---------------------------------------------------------------------------
  // Zapisane diagnozy — wczytanie / usunięcie / wyczyszczenie
  // ---------------------------------------------------------------------------
  // Czy bieżąca sesja ma niezapisane zmiany względem bieżącego wpisu?
  // (Brak bieżącego wpisu + jakiekolwiek dane = też „niezapisane".)
  const hasUnsavedSessionChanges = useCallback(() => {
    const hasAnyData =
      Boolean(interpretation) ||
      patientInfo.name.trim() !== '' ||
      Object.values(subtestScores).some(v => v !== '') ||
      Object.values(compositeScores).some(v => v !== '');
    if (!hasAnyData) return false;
    const currentEntry = currentDiagnosisId
      ? savedDiagnoses.find(d => d.id === currentDiagnosisId)
      : undefined;
    if (!currentEntry) return true;
    const snapshot = (p: unknown, s: unknown, c: unknown, i: unknown) => JSON.stringify({ p, s, c, i });
    return snapshot(patientInfo, subtestScores, compositeScores, interpretation)
      !== snapshot(currentEntry.patientInfo, currentEntry.subtestScores, currentEntry.compositeScores, currentEntry.interpretation);
  }, [interpretation, patientInfo, subtestScores, compositeScores, currentDiagnosisId, savedDiagnoses]);

  // WCZYTAJ = naturalne przejście: dane wpisu wchodzą przez istniejące
  // handlery + setInterpretation, wpis staje się bieżącym (kolejne edycje go
  // aktualizują), włącza się focus mode; scroll do panelu wyniku robi
  // auto-scroll w ResultsDisplay (zmiana referencji interpretacji po mount).
  const handleLoadDiagnosis = useCallback((id: string) => {
    const entry = savedDiagnoses.find(d => d.id === id);
    if (!entry) return;

    if (hasUnsavedSessionChanges()) {
      const ok = window.confirm(
        'Bieżąca sesja ma niezapisane zmiany — wczytanie zapisanej diagnozy je nadpisze. Kontynuować?'
      );
      if (!ok) return;
    }

    (['name', 'birthDate', 'testDate', 'examiner', 'institution'] as Array<keyof PatientInfo>).forEach(field => {
      handlePatientInfoChange(field, entry.patientInfo?.[field] ?? '');
    });
    Object.keys(subtestNames).forEach(key => {
      handleSubtestScoreChange(key, entry.subtestScores?.[key] ?? '');
    });
    Object.keys(compositeNames).forEach(key => {
      handleCompositeScoreChange(key, entry.compositeScores?.[key] ?? '');
    });

    // Artefakty poprzedniej generacji (surowy markdown, thinking, widok raw)
    // nie należą do wczytywanego wpisu — czyścimy, żeby nie mieszać sesji.
    resetGenerationArtifacts();
    rawStreamTextRef.current = '';
    setRawStreamText('');
    setStreamingText('');

    setInterpretation(entry.interpretation);
    setDiagnosisEdited((entry.editedSections?.length ?? 0) > 0);
    setCurrentDiagnosisId(entry.id);
    // Wczytanie diagnozy otwiera rozszerzony widok (plan 4.2) — fade (brak
    // origin rect, bo panel dopiero powstaje przy pojawieniu interpretacji).
    setFullscreenOriginRect(null);
    setShowFullscreenResult(true);
    setDebugLogs(prev => [
      ...prev,
      `[${new Date().toLocaleTimeString('pl-PL')}] 📂 Wczytano zapisaną diagnozę: ${entry.patientInfo?.name?.trim() || 'Pacjent'}`,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedDiagnoses, hasUnsavedSessionChanges,
    handlePatientInfoChange, handleSubtestScoreChange, handleCompositeScoreChange,
    resetGenerationArtifacts, setRawStreamText, setStreamingText,
    setInterpretation, setDebugLogs,
  ]);

  const handleDeleteDiagnosis = useCallback((id: string) => {
    removeDiagnosis(id);
    setSavedDiagnoses(listSavedDiagnoses());
    if (currentDiagnosisId === id) setCurrentDiagnosisId(null);
  }, [currentDiagnosisId]);

  const handleClearAllDiagnoses = useCallback(() => {
    clearDiagnoses();
    setSavedDiagnoses([]);
    setCurrentDiagnosisId(null);
  }, []);

  const downloadReport = useCallback((format: 'txt' | 'docx' | 'markdown' | 'pdf' = 'txt') => {
    if (!interpretation) {
      alert('Najpierw wygeneruj interpretację');
      return;
    }

    const exportData: ExportData = {
      patientInfo,
      compositeScores,
      subtestScores,
      interpretation: {
        general_assessment: interpretation.general_assessment,
        strengths: interpretation.strengths,
        areas_for_support: interpretation.areas_for_support || interpretation.difficulties || '',
        classroom_implications: interpretation.classroom_implications || interpretation.school_functioning || '',
        teacher_recommendations: interpretation.teacher_recommendations,
        parent_recommendations: interpretation.parent_recommendations
      },
      compositeNames,
      subtestNames,
      getScoreInterpretation: (score, type) => getScoreInterpretation(score, type as 'composite' | 'subtest')
    };

    switch (format) {
      case 'docx':
        ExportUtils.exportToDocx(exportData).catch((err) => {
          alert(err instanceof Error ? err.message : 'Nie udało się załadować modułu eksportu');
        });
        break;
      case 'markdown':
        ExportUtils.exportToMarkdown(exportData);
        break;
      case 'pdf':
        ExportUtils.exportToPDF(exportData).catch((err) => {
          alert(err instanceof Error ? err.message : 'Nie udało się załadować modułu eksportu');
        });
        break;
      default:
        ExportUtils.exportToTxt(exportData);
    }

    dispatch({ type: 'SET_EXPORT_DROPDOWN', show: false });
  }, [interpretation, patientInfo, compositeScores, subtestScores]);

  const handleGenerate = useCallback((customPrompt?: string, customSystemPrompt?: string) => {
    // GUARD: ręczne redakcje diagnozy zostaną nadpisane przez nową generację —
    // wymagaj świadomego potwierdzenia (nowa udana generacja zeruje flagę na
    // krawędzi startu aiLoading).
    if (diagnosisEdited) {
      const ok = window.confirm('Masz ręczne zmiany w diagnozie — nowa generacja je nadpisze. Kontynuować?');
      if (!ok) return;
    }

    // WISC-V range validation: block AI generation when any entered score
    // is out of range (subtests 1-19, composite indices 40-160)
    const validationErrors = collectScoreValidationErrors(subtestScores, compositeScores);
    if (validationErrors.length > 0) {
      setDebugLogs(prev => [
        ...prev,
        `[${new Date().toLocaleTimeString('pl-PL')}] ❌ Walidacja wyników nie powiodła się:`,
        ...validationErrors.map(err => `   • ${err}`)
      ]);
      alert(
        'Nie można wygenerować interpretacji — niepoprawne wyniki:\n\n' +
        validationErrors.map(err => `• ${err}`).join('\n')
      );
      return;
    }

    const promptToUse = customPrompt || preparePrompt(false);
    if (!promptToUse) return; // Validation failed inside preparePrompt (e.g. missing scores)
    // System instruction travels separately (v2.1 prompt): from the preview modal
    // when sending a previewed prompt, else from the selected library prompt.
    const systemToUse = customPrompt ? customSystemPrompt : selectedSystemPrompt;
    dispatch({ type: 'GENERATION_ATTEMPTED' });
    // Silnik deterministyczny, tryb shadow (WISCV_ENGINE=shadow na serwerze): niewidoczne
    // wywołanie porównujące z legacy; bez imienia/daty urodzenia, błędy ignorowane.
    void runEngineShadow(patientInfo, compositeScores, subtestScores);
    generateInterpretation(promptToUse, enableStreaming, systemToUse || undefined);
  }, [diagnosisEdited, patientInfo, subtestScores, compositeScores, setDebugLogs, preparePrompt, selectedSystemPrompt, generateInterpretation, enableStreaming]);

  // Retry wiring (wave 2c, pkt 2): useAIIntegration does not expose lastError,
  // so the "Ponów" button is driven by the local generationAttempted flag —
  // it appears once any generation has been started and retryGeneration()
  // itself guards the actual retry budget (max 1 per fresh generation).
  const handleRetryGeneration = useCallback(() => {
    retryGeneration();
  }, [retryGeneration]);

  // Recovered partial stream (wave 2c, pkt 1)
  const handleInsertRecoveredPartial = useCallback(() => {
    if (!recoveredPartialText) return;
    // Sync the raw stream state so the standard "Pełna odpowiedź Markdown"
    // view also has content when an interpretation exists.
    rawStreamTextRef.current = recoveredPartialText;
    setRawStreamText(recoveredPartialText);
    setShowRawResponse(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveredPartialText, setRawStreamText, setShowRawResponse]);

  const handleDiscardRecoveredPartial = useCallback(() => {
    try {
      sessionStorage.removeItem(PARTIAL_STREAM_STORAGE_KEY);
    } catch {
      // best-effort
    }
    dispatch({ type: 'DISMISS_RECOVERED_PARTIAL' });
  }, []);

  const visibleRecoveredPartialText = ui.recoveredPartialDismissed ? null : recoveredPartialText;

  // ---------------------------------------------------------------------------
  // Elementy UI — panel wyniku zawsze w kolumnie 3 (focus-layout usunięty,
  // plan 4.3 A: jedyna metafora rozszerzania to FLIP-overlay dialogu).
  // ---------------------------------------------------------------------------
  const patientFormEl = (
    <PatientInfoForm
      patientInfo={patientInfo}
      onChange={handlePatientInfoChange}
      onImageUpload={handleImageUpload}
      loading={isGlobalLoading}
      loadingMessage={globalLoadingMessage}
      patientCard={cardControl('patient')}
      ocrCard={cardControl('ocr')}
    />
  );
  const subtestEl = (
    <SubtestScoreInput scores={subtestScores} onChange={handleSubtestScoreChange} collapse={cardControl('subtests')} />
  );
  const compositeEl = (
    <CompositeScoreInput scores={compositeScores} onChange={handleCompositeScoreChange} collapse={cardControl('composites')} />
  );
  const profileEl = (
    <CognitiveProfileAnalyzer compositeScores={compositeScores} collapse={cardControl('profile')} />
  );
  const aiControlsEl = (
    <AIConfigProvider value={aiConfigValue}>
      <AIControls
        promptLibrary={promptLibrary}
        selectedPromptId={selectedPromptId}
        setSelectedPromptId={setSelectedPromptId}
        setShowPromptLibrary={setShowPromptLibrary}
        enableStreaming={enableStreaming}
        setEnableStreaming={setEnableStreaming}
        isStreaming={isStreaming}
        preparePrompt={preparePrompt}
        generateInterpretation={handleGenerate}
        onRetryGeneration={handleRetryGeneration}
        canRetry={ui.generationAttempted && !isGlobalLoading}
        loading={isGlobalLoading}
        loadingMessage={globalLoadingMessage}
        collapse={cardControl('ai')}
      />
    </AIConfigProvider>
  );
  const resultsEl = (
    <ResultsDisplay
      isStreaming={isStreaming}
      rawStreamText={rawStreamText}
      streamingText={streamingText}
      streamContainerRef={streamContainerRef}
      interpretation={interpretation}
      showRawResponse={showRawResponse}
      setShowRawResponse={setShowRawResponse}
      savedRawText={savedRawText}
      thinkingProcess={thinkingProcess}
      showExportDropdown={ui.showExportDropdown}
      setShowExportDropdown={setShowExportDropdown}
      onDownloadReport={downloadReport}
      onCopySection={handleCopySection}
      recoveredPartialText={visibleRecoveredPartialText}
      onInsertRecoveredPartial={handleInsertRecoveredPartial}
      onDiscardRecoveredPartial={handleDiscardRecoveredPartial}
      onOpenFullscreen={handleOpenFullscreen}
      onCopyAll={interpretation ? handleCopyAll : undefined}
      diagnosisEdited={diagnosisEdited}
      answeredModel={answeredModel}
      generationNotice={generationNotice}
    />
  );
  const savedDiagnosesEl = (
    <SavedDiagnosesCard
      diagnoses={savedDiagnoses}
      currentId={currentDiagnosisId}
      disabled={isGlobalLoading || isStreaming}
      onLoad={handleLoadDiagnosis}
      onDelete={handleDeleteDiagnosis}
      onClearAll={handleClearAllDiagnoses}
      collapse={cardControl('saved')}
    />
  );
  const debugEl = <DebugLog logs={debugLogs} onClear={() => setDebugLogs([])} collapse={cardControl('debug')} />;

  return (
    <>
      {/* app-canvas = tło „szczotkowanego szkła” (gradient + faktura na stałej warstwie).
          overflow-x-clip zamiast hidden: hidden robi z korzenia kontener przewijania
          i psuje `position: sticky` nagłówka. Bez `relative z-10` na treści: ten kontekst
          warstw zamykał modale (z-9999) pod plakietką konta (z-50). */}
      <div className="app-canvas min-h-screen text-txt-primary flex flex-col p-3 sm:p-5 overflow-x-clip">

      <div className="w-full max-w-[1600px] mx-auto flex flex-col gap-5 lg:gap-6">
        <Header onLoadTestData={loadTestData} />

        {/* Main Content Dashboard — zawsze 3 kolumny (panel wyniku w kolumnie 3).
            Focus-layout usunięty (plan 4.3 A): rozszerzanie = FLIP-overlay
            FullscreenResultDialog. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 items-start">
          {/* Column 1 - Inputs */}
          <div className="flex flex-col gap-5 lg:gap-6 lg:max-h-[calc(100vh-8.5rem)] lg:overflow-y-auto lg:-mx-2 lg:px-2 lg:pb-3">
            {patientFormEl}
            {subtestEl}
            {compositeEl}
          </div>

          {/* Column 2 - Cognitive Profile Analyzer */}
          <div className="flex flex-col gap-5 lg:gap-6 lg:max-h-[calc(100vh-8.5rem)] lg:overflow-y-auto lg:-mx-2 lg:px-2 lg:pb-3">
            {profileEl}
          </div>

          {/* Column 3 - AI & Results */}
          <div className="flex flex-col gap-5 lg:gap-6 lg:max-h-[calc(100vh-8.5rem)] lg:overflow-y-auto lg:-mx-2 lg:px-2 lg:pb-3">
            {aiControlsEl}
            {/* Wrapper z refem = origin rect FLIP dla auto-otwarcia arkusza
                na pierwszym tokenie (W2, plan 5.2). */}
            <div ref={resultsPanelWrapRef}>{resultsEl}</div>
            {savedDiagnosesEl}
            {debugEl}
          </div>
        </div>

        {/* Pełnoekranowy widok wyniku (Radix Dialog) — rozszerzanie FLIP */}
        <FullscreenResultDialog
          open={showFullscreenResult}
          onOpenChange={setShowFullscreenResult}
          originRect={fullscreenOriginRect}
          patientName={patientInfo.name}
          interpretation={interpretation}
          isStreaming={isStreaming}
          streamingText={streamingText}
          rawText={savedRawText || rawStreamText}
          onCopyAll={handleCopyAll}
          onCopyMarkdown={handleCopyMarkdown}
          onDownloadReport={downloadReport}
          onCopySection={handleCopySection}
          onSaveEditedMarkdown={saveEditedMarkdownHandler}
          diagnosisEdited={diagnosisEdited}
          generationStatus={lastGenerationStatus}
          onRetry={handleRetryGeneration}
        />

        {/* Modals */}
        {showPromptPreview && (
          <PromptPreviewModal
            previewPrompt={previewPrompt}
            systemPrompt={previewSystemPrompt}
            onClose={() => setShowPromptPreview(false)}
            onGenerate={() => {
              setShowPromptPreview(false);
              handleGenerate(previewPrompt, previewSystemPrompt);
            }}
            onCopy={copyPromptToClipboard}
          />
        )}

        {showPromptLibrary && (
          <PromptLibraryModal
            promptLibrary={promptLibrary}
            onClose={() => setShowPromptLibrary(false)}
            createNewPrompt={createNewPrompt}
            viewPrompt={viewPrompt}
            editPrompt={editPrompt}
            deletePrompt={deletePrompt}
            onSelectPrompt={setSelectedPromptId}
            selectedPromptId={selectedPromptId}
          />
        )}

        {editingPrompt && (
          <PromptEditorModal
            prompt={editingPrompt}
            onChange={(updated) => setEditingPrompt(updated)}
            onSave={() => savePrompt(editingPrompt)}
            onCancel={() => setEditingPrompt(null)}
          />
        )}

        {/* Toast — portal do <body> (poza kontekstem warstw kolumn), nad modalami */}
        {ui.copyToast.show && <CopyToast message={ui.copyToast.message} />}
      </div>
    </div>
    </>
  );
};

export default SimpleWISCVSystem;
